// Updated SurchargeCess.tsx
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import Table from "../../../core/common/dataTable/index";
import CollapseHeader from "../../../core/common/collapse-header/collapse-header";
import PageHeader from "../../../core/common/commonComponents/PageHeader";
import StatusFilterHeader from "../../../core/common/commonComponents/StatusFilterHeader";
import DeleteModal1 from "../../../core/modals/deleteModal1";
import { exportToCSV, exportToExcel } from "../../../core/common/GlobalFunctions/exportTableData";
import { message } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "../../../core/data/redux/store";
import { filterData } from "../../../core/common/GlobalFunctions/tableFilters";
import SurchargeCessModal from "./modal/Surcharge&CessModel";
import { deleteTdsSurchargeCess, fetchTdsSurchargeCessList } from "../../../core/data/redux/payroll/TDS/tdsSurchargeCess/tdsSurchargeCessActions";
import { decryptData } from "../../../core/utils/encryptUtils";

const SurchargeCess = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { surchargeCessList } = useSelector((state: any) => state.TDS.tdsSurchargeCess);
  const [selectedStatus, setSelectedStatus] = useState("active");
  const [selectedSort, setSelectedSort] = useState<any>("");
  const [tableKey, setTableKey] = useState(0);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [modalType, setModalType] = useState<'edit' | 'create' | ''>('');
  const [selectedRowData, setSelectedRowData] = useState<any>(null);

  // Permission
  const encryptedPermissions = localStorage.getItem('currentPermissions');
  const permissions: string[] = Array.isArray(decryptData(encryptedPermissions || '')) ? decryptData(encryptedPermissions || '') : [];

  const canAdd =
    permissions.includes("Add") || permissions.includes("AllowAll");
  const canView =
    permissions.includes("View") || permissions.includes("AllowAll");
  const canEdit =
    permissions.includes("Edit") || permissions.includes("AllowAll");
  const canDelete =
    permissions.includes("Delete") || permissions.includes("AllowAll");
  const canExport =
    permissions.includes("Export") || permissions.includes("AllowAll");

  const showModal = (type: 'edit' | 'create') => {
    setModalType(type);
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setModalType('');
    setIsModalVisible(false);
  };

  useEffect(() => {
    dispatch(fetchTdsSurchargeCessList());
  }, [dispatch]);


  const filteredData = useMemo(() => {
    const filters = selectedStatus !== "All" ? { Status: selectedStatus } : {};
    return filterData(surchargeCessList, filters, "CreatedAt", "CreatedAt", selectedSort);
  }, [surchargeCessList, selectedStatus, selectedSort]);

  useEffect(() => {
    setTableKey((prevKey) => prevKey + 1);
  }, [dispatch, selectedStatus, selectedSort, surchargeCessList]);

  const exportData = filteredData.map((item: any) => ({
    IncomeThresholdForSurcharge1: item.IncomeThresholdForSurcharge1 || 0,
    SurchargeRate1_Percentage: item.SurchargeRate1_Percentage || 0,
    IncomeThresholdForSurcharge2: item.IncomeThresholdForSurcharge2 || 0,
    SurchargeRate2_Percentage: item.SurchargeRate2_Percentage || 0,
    HealthAndEducationCessRate_Percentage: item.HealthAndEducationCessRate_Percentage || 0,
    Status: item.Status || "Inactive",
  }));

  const handleDelete = async (id: number) => {
    try {
      const result = await dispatch(deleteTdsSurchargeCess(id));
      if (result.meta.requestStatus === 'fulfilled') {
        message.success('Surcharge/Cess deleted successfully');
        setIsDeleteModalVisible(false);
        dispatch(fetchTdsSurchargeCessList());
      } else {
        message.error('Delete failed');
      }
    } catch (error) {
      message.error("Something went wrong");
    }
  };

  const columns = [
    {
      title: "Financial Year",
      dataIndex: "FinancialYearName",
    },
    {
      title: "Income Threshold 1",
      dataIndex: "IncomeThresholdForSurcharge1",
    },
    {
      title: "Surcharge Rate 1 (%)",
      dataIndex: "SurchargeRate1_Percentage",
    },
    {
      title: "Income Threshold 2",
      dataIndex: "IncomeThresholdForSurcharge2",
    },
    {
      title: "Surcharge Rate 2 (%)",
      dataIndex: "SurchargeRate2_Percentage",
    },
    {
      title: "Edu Cess Rate (%)",
      dataIndex: "HealthAndEducationCessRate_Percentage",
    },
    {
      title: "Status",
      dataIndex: "Status",
      render: (status: string) => (
        <span className={`badge ${status === 'active' ? 'badge-success' : 'badge-danger'}`}>
          {status}
        </span>
      ),
    },
    {
      title: "Action",
      render: (_: any, record: any) => (
        <div className="action-icon d-inline-flex">
          {canEdit && (
            <button
              className="me-2"
              onClick={() => {
                showModal("edit");
                setSelectedRowData(record);
              }}
              title="Edit"
            >
              <i className="ti ti-edit" />
            </button>
          )}
          {canDelete && (
            <button
              disabled={record.Status === "inactive"}
              onClick={() => {
                setSelectedRowData(record);
                setIsDeleteModalVisible(true);
              }}
              title="Delete"
            >
              <i className="ti ti-trash" />
            </button>
          )}
        </div>
      ),
    },
  ];

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          <PageHeader
            title="TDS Surcharge & Cess"
            breadcrumbs={[
              { label: <i className="ti ti-smart-home" />, path: all_routes.adminDashboard },
              { label: "TDS" },
              { label: "Surcharge & Cess", isActive: true },
            ]}
            listViewPath="#"
            onExportCSV={canExport ? () => exportToCSV(exportData, "TdsSurchargeCess") : undefined}
            onExportExcel={canExport ? () => exportToExcel(exportData, "TdsSurchargeCess") : undefined}
            onAdd={canAdd ? () => showModal("create") : undefined}
            addLabel="Add Surcharge/Cess"
            exportEnabled={canExport}
            addButtonEnabled={canAdd}
            rightExtra={<CollapseHeader />}
          />

          <div className="card">
            <StatusFilterHeader
              title="Surcharge & Cess List"
              selectedStatus={selectedStatus}
              setSelectedStatus={setSelectedStatus}
              selectedSort={selectedSort}
              setSelectedSort={setSelectedSort}
            />
            <div className="card-body p-0">
              <Table key={tableKey} dataSource={filteredData} columns={columns} Selection={false} />
            </div>
          </div>
        </div>
        <div className="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3">
          <p className="mb-0">2024 - 2025© hrmtree.</p>
          <p>
            Developed By {" "}
            <Link to="#" className="text-primary">
              Leadchain Systems
            </Link>
          </p>
        </div>
      </div>

      <SurchargeCessModal
        modalType={modalType}
        handleCancel={handleCancel}
        isModalVisible={isModalVisible}
        selectedRowData={selectedRowData}
      />

      <DeleteModal1
        isVisible={isDeleteModalVisible}
        onClose={() => setIsDeleteModalVisible(false)}
        onConfirm={() => selectedRowData?.SurchargeCessID && handleDelete(selectedRowData?.SurchargeCessID)}
      />
    </>
  );
};

export default SurchargeCess;
